import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { Building2 } from 'lucide-react';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useResourceActions } from '@/hooks/useResourceActions';
import { useSchoolPicker } from '@/hooks/usePickers';
import { campusesApi } from '@/services/api/endpoints/schools';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Campus } from '@/types/domain';
import { orDash } from '@/utils/format';

const schema = z.object({
  code: z.string().min(1, 'Code is required').max(32),
  name: z.string().min(1, 'Name is required').max(200),
  email: z.string().email('Enter a valid e-mail address').or(z.literal('')),
  phone: z.string().max(32),
  principalName: z.string().max(200),
  city: z.string().max(100),
  primary: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  code: '',
  name: '',
  email: '',
  phone: '',
  principalName: '',
  city: '',
  primary: false,
};

export function CampusesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const table = useTableQuery();

  const [editing, setEditing] = useState<Campus | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Campus | null>(null);
  const [schoolFilter, setSchoolFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<Campus['status'] | ''>('');

  const schools = useSchoolPicker();

  const params = {
    ...table.params,
    ...(schoolFilter !== '' ? { schoolId: schoolFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.campuses.list(params),
    queryFn: () => campusesApi.list(params),
  });

  const { statusMutation, deleteMutation, invalidateAll } = useResourceActions({
    invalidates: [queryKeys.campuses.all, queryKeys.gradeOfferings.all, queryKeys.sections.all],
    changeStatus: campusesApi.changeStatus,
    remove: campusesApi.remove,
    label: 'Campus',
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      // Empty strings are absent values, not blanks to store.
      const blank = (value: string) => (value === '' ? undefined : value);
      const common = {
        name: values.name,
        email: blank(values.email),
        phone: blank(values.phone),
        principalName: blank(values.principalName),
        address: { city: blank(values.city) },
        primary: values.primary,
      };
      return editing === null
        ? campusesApi.create({ ...common, code: values.code })
        : campusesApi.update(editing.id, common);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Campus created' : 'Campus updated');
      close();
      invalidateAll();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  function openCreate() {
    setEditing(null);
    form.reset(EMPTY);
    setOpen(true);
  }

  function openEdit(campus: Campus) {
    setEditing(campus);
    form.reset({
      code: campus.code,
      name: campus.name,
      email: campus.email ?? '',
      phone: campus.phone ?? '',
      principalName: campus.principalName ?? '',
      city: campus.address?.city ?? '',
      primary: campus.primary,
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
    form.reset(EMPTY);
  }

  const columns: Column<Campus>[] = [
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (campus) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{campus.code}</span>
          {campus.primary && <Badge tone="info">Primary</Badge>}
        </div>
      ),
    },
    { key: 'name', header: 'Name', sortKey: 'name', render: (campus) => campus.name },
    { key: 'school', header: 'School', render: (campus) => orDash(campus.schoolName) },
    { key: 'city', header: 'City', render: (campus) => orDash(campus.address?.city) },
    { key: 'principal', header: 'Principal', render: (campus) => orDash(campus.principalName) },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (campus) => <StatusBadge status={campus.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (campus) => (
        <div className="flex justify-end gap-1">
          {can(PERMISSIONS.CAMPUS_UPDATE) && (
            <>
              <Button size="sm" variant="ghost" onClick={() => openEdit(campus)}>
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={statusMutation.isPending}
                onClick={() =>
                  statusMutation.mutate({
                    id: campus.id,
                    payload: { status: campus.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
                  })
                }
              >
                {campus.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
          {can(PERMISSIONS.CAMPUS_DELETE) && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(campus)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Campuses"
        description="Physical sites belonging to your school. Grades are organised beneath a campus."
        action={can(PERMISSIONS.CAMPUS_CREATE) ? <Button onClick={openCreate}>New campus</Button> : undefined}
        icon={<Building2 className="h-5 w-5" aria-hidden="true" />}
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search campuses…' }}
        filters={
          <>
            <FilterSelect
              label="School"
              value={schoolFilter}
              onChange={(value) => {
                setSchoolFilter(value);
                table.setPage(0);
              }}
              options={(schools.data ?? []).map((school) => ({ value: school.id, label: school.name }))}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(campus) => campus.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No campuses yet"
          emptyDescription="Add a campus before creating grades and sections."
        />
      </ListPage>

      <Modal
        open={open}
        title={editing === null ? 'New campus' : `Edit ${editing.name}`}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              disabled={editing !== null}
              hint={editing !== null ? 'Fixed after creation' : 'Unique within the school'}
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField
              label="Name"
              required
              error={form.formState.errors.name?.message}
              {...form.register('name')}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="E-mail"
              type="email"
              error={form.formState.errors.email?.message}
              {...form.register('email')}
            />
            <TextField label="Phone" error={form.formState.errors.phone?.message} {...form.register('phone')} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Principal"
              error={form.formState.errors.principalName?.message}
              {...form.register('principalName')}
            />
            <TextField label="City" error={form.formState.errors.city?.message} {...form.register('city')} />
          </div>
          <label className="flex items-start gap-2 text-sm text-muted">
            <input type="checkbox"className="mt-0.5"{...form.register('primary')} />
            <span>
              Principal campus
              <span className="block text-xs text-muted">
                Any campus currently marked principal will be cleared.
              </span>
            </span>
          </label>
        </form>
      </Modal>
      <ConfirmDialog
        open={deleting !== null}
        title="Delete campus"
        message={
          deleting === null
            ? ''
            : `Delete ${deleting.name}? This is refused while grade offerings reference it, since sections and enrolments hang off those.`
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
